import { Fragment, useState } from 'react';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { PrimaryTitleGray } from '../../../app/theme/tokens/colors';
import { InputText, PrimaryButton, Select } from '../../../shared/ui/Inputs';
import { Spinner } from '../../../shared/ui/Spinners';
import { BasicPagination } from '../../../shared/ui/pagination/basic';
import { getAllGasesProducts } from '../../../services/recharge/rechargeService';
import {
  displayOrDash,
  formatDocumentOrDash,
} from '../../../services/patients/patientsService';
import { usePortalStore } from '../../../stores/portalStore';
import { useRechargeStore } from '../store/rechargeStore';
import { formatCPF } from '../../../utils/format';
import {
  IconButton,
  Paper,
  Radio,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ExchangeIcon } from '../../../shared/icons/ExchangeIcon';
import { isGenericCylinder } from '../../../utils/orders';
import { useCompaniesCatalog } from '../../../hooks/useCompaniesCatalog';

const PatientsListRoot = styled.div`
  overflow: auto;
  flex: 1;
  min-height: 0;
`;
const PatientList = styled.ul`
  list-style: none;
  padding: 0px;
  margin: 0px;
  box-sizing: border-box;
`;
const PatientItemList = styled.li`
  padding: 5px;
  box-sizing: border-box;
`;
const PatientItemHeader = styled.div`
  border-radius: 10px;
  position: relative;
  box-sizing: border-box;
  box-shadow: rgb(207 207 207) 0px 1px 3px;
  padding: 8px 20px;
  @media screen and (max-width: 600px) {
    padding: 10px;
  }
`;
const PatientItemHeaderTitle = styled.p`
  padding: 0px;
  margin: 0px;
  box-sizing: border-box;
  font-weight: 500;
  font-size: 14px;
  color: ${PrimaryTitleGray};
`;
const PatientItemBody = styled.div`
  box-sizing: border-box;
  margin-top: 0px;
  height: 0px;
  overflow: hidden;
`;

const PatientInsideBody = styled.div`
  margin-top: 15px;
  box-sizing: border-box;
  overflow: visible;

  width: 100%;
`;

const tableContainerSx = {
  border: '1px solid #e6e6e6',
  borderRadius: 1,
  overflowX: 'auto',
};

const tableHeaderCellSx = {
  backgroundColor: '#767676',
  color: 'white',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

const MuiRechargeTable = ({ header, data, expandVisible }) => (
  <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
    <MuiTable
      stickyHeader
      size="small"
      aria-label="Cilindros do paciente"
      sx={{ minWidth: 680 }}
    >
      <TableHead>
        <TableRow>
          {header.map((column) => (
            <TableCell key={column} sx={tableHeaderCellSx}>
              {column}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((row, rowIndex) => (
          <Fragment key={rowIndex}>
            <TableRow hover>
              {Object.entries(row)
                .filter(([field]) => field !== 'expandPanel')
                .map(([field, value]) => (
                  <TableCell
                    key={field}
                    sx={{ verticalAlign: 'middle', py: 1 }}
                  >
                    {value}
                  </TableCell>
                ))}
            </TableRow>
            {expandVisible?.[2 * rowIndex + 1] ? (
              <TableRow>
                <TableCell
                  colSpan={header.length}
                  sx={{ backgroundColor: '#fafafa', p: 1.5 }}
                >
                  {row.expandPanel}
                </TableCell>
              </TableRow>
            ) : null}
          </Fragment>
        ))}
      </TableBody>
    </MuiTable>
  </TableContainer>
);

const MuiExchangeCylinderTable = ({ header, fields, data }) => (
  <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
    <MuiTable
      stickyHeader
      size="small"
      aria-label="Cilindros disponíveis para troca"
    >
      <TableHead>
        <TableRow>
          {header.map((column, columnIndex) => (
            <TableCell
              key={`${column}-${columnIndex}`}
              align={columnIndex === 0 ? 'center' : 'left'}
              sx={tableHeaderCellSx}
            >
              {column}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((row, rowIndex) => (
          <TableRow hover key={rowIndex}>
            {fields.map((field, columnIndex) => (
              <TableCell
                key={field}
                align={columnIndex === 0 ? 'center' : 'left'}
                sx={{ verticalAlign: 'middle', py: 0.75 }}
              >
                {row[field]}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </MuiTable>
  </TableContainer>
);

export const ListPatientsRecharge = () => {
  useMediaQuery({ query: '(max-width: 1224px)' });
  const setSelectedPatient = useRechargeStore(
    (state) => state.setSelectedPatient
  );
  const listState = useRechargeStore((state) => state.listState);
  const setListState = useRechargeStore((state) => state.setListState);
  const patients = useRechargeStore((state) => state.patients);
  const fetchPatients = useRechargeStore((state) => state.fetchPatients);
  const patientsLoading = useRechargeStore((state) => state.patientsLoading);
  const rows = useRechargeStore((state) => state.rows);
  const setReChargerActions = useRechargeStore(
    (state) => state.setReChargerActions
  );
  const setReChargerActionsQuantity = useRechargeStore(
    (state) => state.setReChargerActionsQuantity
  );
  const setCurrentAlertError = usePortalStore(
    (state) => state.setCurrentAlertError
  );
  const rechargerActions = useRechargeStore((state) => state.rechargerActions);
  const exChangeCylinders = useRechargeStore(
    (state) => state.exChangeCylinders
  );
  const setExchangeCylinders = useRechargeStore(
    (state) => state.setExchangeCylinders
  );
  const expandVisible = useRechargeStore((state) => state.expandVisible);
  const setExpandVisible = useRechargeStore((state) => state.setExpandVisible);
  const setPagination = useRechargeStore((state) => state.setPagination);
  const page = useRechargeStore((state) => state.page);
  const pageSize = useRechargeStore((state) => state.pageSize);
  const [isLoading, setIsLoading] = useState(false);
  const [exchangeLoading, setExchangeLoading] = useState({});
  const navigate = useNavigate();
  const { resolveCompanyName } = useCompaniesCatalog();
  const setReChargerExchangeActions = useRechargeStore(
    (state) => state.setReChargerExchangeActions
  );

  const exchangeKey = (patientIdx, cylinderIdx) =>
    `${patientIdx}_${cylinderIdx}`;

  const getDefaultAction = (cylinder) => {
    if (cylinder?.selectionMode === 'exchange') return 'troca';
    if (isGenericCylinder(cylinder)) return 'troca';
    return 'recarga';
  };

  const getAvailableActions = (cylinder) => {
    const actions =
      Array.isArray(cylinder?.availableActions) &&
      cylinder.availableActions.length > 0
        ? cylinder.availableActions
        : getDefaultAction(cylinder) === 'troca'
        ? ['troca']
        : ['recarga', 'troca'];

    return actions.filter(
      (action) => action === 'recarga' || action === 'troca'
    );
  };

  const formatCylinderVolume = (cylinder) => {
    const size = String(cylinder?.size ?? '').trim();
    return size ? `${size}m³` : '-';
  };

  const SalesTooltipIcon = ({ text }) => {
    if (!text) return null;

    return (
      <Tooltip
        title={<span style={{ whiteSpace: 'pre-line' }}>{text}</span>}
        arrow
        placement="right"
        componentsProps={{
          tooltip: {
            sx: {
              maxWidth: 420,
              fontSize: 12,
              lineHeight: 1.5,
            },
          },
        }}
      >
        <IconButton
          size="small"
          color="primary"
          style={{ marginLeft: 6, padding: 2 }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  const ensureExchangeCylindersLoaded = async (patientIdx, cylinderIdx) => {
    const key = exchangeKey(patientIdx, cylinderIdx);
    if (exchangeLoading[key]) {
      return;
    }

    const existing = useRechargeStore.getState().exChangeCylinders;
    if (existing?.[patientIdx]?.[cylinderIdx]) {
      return;
    }

    setExchangeLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const references = await getAllGasesProducts();
      console.log('references fetched for exchange', references);

      const latest = useRechargeStore.getState().exChangeCylinders;
      const nextExchange = { ...latest };
      nextExchange[patientIdx] = nextExchange[patientIdx]
        ? { ...nextExchange[patientIdx] }
        : {};
      nextExchange[patientIdx][cylinderIdx] = [...references];
      setExchangeCylinders(nextExchange);
    } catch (error) {
      console.warn('Erro ao buscar cilindros para troca:', error);
      setCurrentAlertError('Erro ao carregar lista de cilindros para troca');
    } finally {
      setExchangeLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const toggle = (idx) => {
    const newList = [...listState];
    newList[idx] = !newList[idx];
    for (var i = 0; i < newList.length; i++) {
      if (i != idx) {
        newList[i] = false;
      }
    }
    setListState([...newList]);
    if (patients[idx] && newList[idx]) {
      setSelectedPatient(patients[idx]);
    } else {
      setSelectedPatient(null);
    }

    const nextExpandVisible = { ...expandVisible };
    Object.keys(nextExpandVisible).forEach((pId) => {
      nextExpandVisible[pId] = Array.isArray(nextExpandVisible[pId])
        ? [...nextExpandVisible[pId]]
        : [];
      if (pId != idx) {
        for (var x = 0; x < nextExpandVisible[pId].length; x++) {
          nextExpandVisible[pId][x] = false;
        }
      }
    });

    if (patients[idx] && newList[idx]) {
      nextExpandVisible[idx] = Array.isArray(nextExpandVisible[idx])
        ? [...nextExpandVisible[idx]]
        : [];

      const cylindersCount = Array.isArray(patients[idx]?.cylinders)
        ? patients[idx].cylinders.length
        : 0;
      for (var jj = 0; jj < cylindersCount; jj++) {
        const action =
          rechargerActions?.[idx]?.[jj]?.action ??
          getDefaultAction(patients[idx].cylinders[jj]);
        if (action !== 'troca') {
          continue;
        }
        nextExpandVisible[idx][2 * jj + 1] = true;
        ensureExchangeCylindersLoaded(idx, jj);
      }
    }
    setExpandVisible(nextExpandVisible);
  };

  const toggleTable = (value, idx, jj, cylinder) => {
    const nextExpandVisible = { ...expandVisible };
    nextExpandVisible[idx] = Array.isArray(nextExpandVisible[idx])
      ? [...nextExpandVisible[idx]]
      : [];
    nextExpandVisible[idx][2 * jj + 1] = value === 'troca';

    setReChargerActions(idx, jj, cylinder, value);
    setExpandVisible(nextExpandVisible);

    if (nextExpandVisible[idx][2 * jj + 1]) {
      (async () => {
        if (exChangeCylinders[idx] && exChangeCylinders[idx][jj]) {
          return;
        }
        await ensureExchangeCylindersLoaded(idx, jj);
      })();
    }
  };

  const toggleTableQuantity = (value, idx, jj, cylinder) => {
    if (value === '') {
      setReChargerActionsQuantity(idx, jj, cylinder, null);
      return;
    }
    const parsed = parseInt(value, 10);
    setReChargerActionsQuantity(
      idx,
      jj,
      cylinder,
      Number.isFinite(parsed) ? parsed : null
    );
  };

  const toggleOnlyTable = (value, idx, jj) => {
    const nextExpandVisible = { ...expandVisible };
    nextExpandVisible[idx] = Array.isArray(nextExpandVisible[idx])
      ? [...nextExpandVisible[idx]]
      : [];
    if (nextExpandVisible[idx][2 * jj + 1]) {
      nextExpandVisible[idx][2 * jj + 1] = false;
    } else {
      nextExpandVisible[idx][2 * jj + 1] = value === 'troca';
    }
    setExpandVisible(nextExpandVisible);

    if (nextExpandVisible[idx][2 * jj + 1]) {
      ensureExchangeCylindersLoaded(idx, jj);
    }
  };
  const AvailableExchangeCylinder = ({ patientIdx, cylinderIdx }) => {
    const key = exchangeKey(patientIdx, cylinderIdx);
    const isExchangeLoading = !!exchangeLoading[key];
    const headers = ['', 'Produto', 'Volume'];
    const fields = ['select', 'product', 'volume'];
    const setupExchange = (value, checked) => {
      if (checked) {
        setReChargerExchangeActions(
          patientIdx,
          cylinderIdx,
          exChangeCylinders[patientIdx][cylinderIdx][parseInt(value)],
          value
        );
        // expandVisible[patientIdx][2 * cylinderIdx + 1] = false;
        // setExpandVisible(expandVisible);
      }
    };
    return (
      <div style={{ boxSizing: 'border-box' }}>
        {isExchangeLoading &&
        (!exChangeCylinders[patientIdx] ||
          !exChangeCylinders[patientIdx][cylinderIdx]) ? (
          <div
            style={{
              width: '100%',
              textAlign: 'center',
              overflow: 'hidden',
              padding: '20px',
              boxSizing: 'border-box',
            }}
          >
            <Spinner></Spinner>
          </div>
        ) : null}
        {exChangeCylinders[patientIdx] &&
        exChangeCylinders[patientIdx][cylinderIdx] ? (
          <>
            <MuiExchangeCylinderTable
              header={headers}
              fields={fields}
              data={exChangeCylinders[patientIdx][cylinderIdx].map(
                (x, exIds) => {
                  return {
                    select: (
                      <Radio
                        size="small"
                        checked={
                          parseInt(
                            rechargerActions?.[patientIdx]?.[cylinderIdx]
                              ?.newCylinderIdx
                          ) === exIds
                        }
                        value={exIds}
                        onChange={(e) =>
                          setupExchange(e.target.value, e.target.checked)
                        }
                        name={`exchange_cylinders_${patientIdx}_${cylinderIdx}`}
                        inputProps={{
                          'aria-label': `Selecionar ${
                            x.description ?? 'cilindro'
                          } para troca`,
                        }}
                      />
                    ),
                    //type: x.type ?? '',
                    product: x.description ?? '',
                    volume: x.size ?? '',
                  };
                }
              )}
            ></MuiExchangeCylinderTable>
          </>
        ) : (
          <></>
        )}
      </div>
    );
  };
  const doPagination = async (pagination) => {
    const nextPage = parseInt(String(pagination.page ?? 0), 10);
    const nextPageSize = parseInt(String(pagination.pageSize ?? 10), 10);

    try {
      setIsLoading(true);
      setPagination(nextPage, nextPageSize);
      await fetchPatients();
    } finally {
      setIsLoading(false);
    }
  };
  const moveNext = () => {
    const allOps = Object.values(rechargerActions)
      .map((x) => Object.values(x))
      .flat();

    const hasTrocaMissingNewCylinder =
      allOps.filter(
        (x) =>
          x?.action === 'troca' &&
          (x?.quantity ?? 0) > 0 &&
          x?.newCylinder === undefined
      ).length > 0;

    const hasTrocaMissingQuantity =
      allOps.filter(
        (x) =>
          x?.action === 'troca' &&
          x?.newCylinder !== undefined &&
          (x?.quantity ?? 0) <= 0
      ).length > 0;

    if (hasTrocaMissingNewCylinder) {
      setCurrentAlertError('Selecione o cilindro para troca');
      return;
    }

    if (hasTrocaMissingQuantity) {
      setCurrentAlertError('Selecione a quantidade do cilindro para troca');
      return;
    }

    const hasValidSelection =
      allOps.filter((x) => x?.action === 'recarga' && (x?.quantity ?? 0) > 0)
        .length > 0 ||
      allOps.filter(
        (x) =>
          x?.action === 'troca' &&
          (x?.quantity ?? 0) > 0 &&
          x?.newCylinder !== undefined
      ).length > 0;

    if (!hasValidSelection) {
      setCurrentAlertError('Selecione a quantidade para prosseguir');
      return;
    }
    navigate('/recharge/confirmRecharge');
  };
  const header = ['Produto', 'Volume', 'Quantidade', 'Ação'];
  const isInactivePatient = (patient) =>
    String(
      patient?.status ??
        patient?.patitent_status ??
        patient?.patient_status ??
        ''
    )
      .trim()
      .toLowerCase() === 'inativo';
  const formatDocument = (value, formatter) => {
    return formatDocumentOrDash(value, formatter, {
      documentType: formatter ? 'cpf' : 'cnpj',
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {!isLoading && !patientsLoading ? (
        <PatientsListRoot>
          <PatientList>
            {patients.map((patient, idx) => {
              const inactivePatient = isInactivePatient(patient);
              const cylinders = Array.isArray(patient?.cylinders)
                ? patient.cylinders
                : [];

              return (
                <PatientItemList key={idx}>
                  <PatientItemHeader className="item-header-responsive">
                    <PatientItemHeaderTitle
                      className="item-title-responsive"
                      style={{
                        opacity:
                          inactivePatient || cylinders.length === 0
                            ? '0.5'
                            : '1',
                      }}
                    >
                      {`${displayOrDash(patient.name)} | CPF: ${formatDocument(
                        patient.cpf,
                        formatCPF
                      )} | ${displayOrDash(
                        resolveCompanyName(
                          patient.cnpj,
                          patient.company ?? patient.cnpj ?? ''
                        )
                      )} | CNPJ: ${formatDocument(patient.cnpj)}`}
                    </PatientItemHeaderTitle>
                    {inactivePatient ? (
                      <div
                        style={{
                          border: '1px solid #f0c36d',
                          background: '#fff8e5',
                          color: '#6b4e00',
                          borderRadius: 6,
                          padding: '10px 12px',
                          fontSize: 13,
                          lineHeight: 1.4,
                          marginTop: 12,
                        }}
                      >
                        Paciente inativo. Não é possível solicitar recarga para
                        este paciente.
                      </div>
                    ) : cylinders.length > 0 ? (
                      listState[idx] ? (
                        <MdKeyboardArrowUp
                          onClick={() => toggle(idx)}
                          style={{
                            cursor: 'pointer',
                            position: 'absolute',
                            right: '3px',
                            top: '3px',
                            color: PrimaryTitleGray,
                            fontSize: '30px',
                          }}
                        ></MdKeyboardArrowUp>
                      ) : (
                        <MdKeyboardArrowDown
                          onClick={() => toggle(idx)}
                          style={{
                            cursor: 'pointer',
                            position: 'absolute',
                            right: '3px',
                            top: '3px',
                            color: PrimaryTitleGray,
                            fontSize: '30px',
                          }}
                        ></MdKeyboardArrowDown>
                      )
                    ) : (
                      <></>
                    )}

                    <PatientItemBody
                      className="item-body-responsive"
                      style={{
                        height:
                          !inactivePatient && listState[idx] ? 'auto' : '0px',
                      }}
                    >
                      <div
                        className="custom-recharge-button-mob"
                        style={{ textAlign: 'right', marginTop: '15px' }}
                      >
                        <PrimaryButton onClick={() => moveNext()}>
                          Prosseguir
                        </PrimaryButton>
                      </div>
                      <PatientInsideBody
                        className="item-body-inside-responsive"
                        style={{ minHeight: '0px' }}
                      >
                        {!inactivePatient && expandVisible[idx] ? (
                          <>
                            <MuiRechargeTable
                              expandVisible={expandVisible[idx]}
                              header={header}
                              data={cylinders.map((c, jj) => ({
                                product: (() => {
                                  if (c.warningText) {
                                    return (
                                      <div
                                        style={{
                                          border: '1px solid #f0c36d',
                                          background: '#fff8e5',
                                          color: '#6b4e00',
                                          borderRadius: 6,
                                          padding: '8px 10px',
                                          fontSize: 12,
                                          lineHeight: 1.35,
                                          maxWidth: 520,
                                        }}
                                      >
                                        {c.warningText}
                                      </div>
                                    );
                                  }
                                  const action =
                                    rechargerActions?.[idx]?.[jj]?.action ??
                                    getDefaultAction(c);
                                  if (action === 'recarga') {
                                    return (
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                          }}
                                        >
                                          <span>
                                            {displayOrDash(c.product)}
                                          </span>
                                        </div>
                                        <SalesTooltipIcon
                                          text={c.tooltipText}
                                        />
                                      </div>
                                    );
                                  }
                                  if (action === 'troca') {
                                    return displayOrDash(
                                      rechargerActions?.[idx]?.[jj]?.newCylinder
                                        ?.description ?? c.product
                                    );
                                  }
                                  return '-';
                                })(),
                                volume: (
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                    }}
                                  >
                                    {(() => {
                                      const action =
                                        rechargerActions?.[idx]?.[jj]?.action ??
                                        getDefaultAction(c);
                                      if (action === 'recarga') {
                                        return (
                                          <div
                                            style={{
                                              margin: 'auto 0px',
                                              width: 'fit-content',
                                            }}
                                          >
                                            {formatCylinderVolume(c)}
                                          </div>
                                        );
                                      }

                                      if (action === 'troca') {
                                        const nc =
                                          rechargerActions?.[idx]?.[jj]
                                            ?.newCylinder;
                                        if (!nc) {
                                          return (
                                            <IconButton
                                              onClick={() =>
                                                toggleOnlyTable(
                                                  'troca',
                                                  idx,
                                                  jj
                                                )
                                              }
                                              color="primary"
                                              size="small"
                                            >
                                              <ExchangeIcon height="20px"></ExchangeIcon>
                                            </IconButton>
                                          );
                                        }
                                        return (
                                          <>
                                            <div
                                              style={{
                                                margin: 'auto 0px',
                                                width: 'fit-content',
                                              }}
                                            >
                                              <IconButton
                                                onClick={() =>
                                                  toggleOnlyTable(
                                                    'troca',
                                                    idx,
                                                    jj,
                                                    c
                                                  )
                                                }
                                                color="primary"
                                              >
                                                <ExchangeIcon height="20px"></ExchangeIcon>
                                              </IconButton>
                                            </div>
                                            <div
                                              style={{
                                                margin: 'auto 0px',
                                                width: 'fit-content',
                                              }}
                                            >
                                              {formatCylinderVolume(nc)}
                                            </div>
                                          </>
                                        );
                                      }

                                      return (
                                        <div
                                          style={{
                                            margin: 'auto 0px',
                                            width: 'fit-content',
                                          }}
                                        >
                                          -
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ),
                                quantity: (
                                  <InputText
                                    type="number"
                                    min={1}
                                    step={1}
                                    placeholder="Selecione"
                                    style={{
                                      minWidth: 150,
                                      width: 150,
                                      boxSizing: 'border-box',
                                    }}
                                    value={
                                      Number.isFinite(
                                        rechargerActions?.[idx]?.[jj]?.quantity
                                      )
                                        ? rechargerActions?.[idx]?.[jj]
                                            ?.quantity
                                        : ''
                                    }
                                    onChange={(e) =>
                                      toggleTableQuantity(
                                        e.target.value,
                                        idx,
                                        jj,
                                        c
                                      )
                                    }
                                    onBlur={(e) => {
                                      const raw = e.target.value;
                                      if (raw === '') return;
                                      const parsed = parseInt(raw, 10);
                                      setReChargerActionsQuantity(
                                        idx,
                                        jj,
                                        c,
                                        (() => {
                                          if (
                                            !Number.isFinite(parsed) ||
                                            parsed <= 0
                                          )
                                            return null;
                                          return parsed;
                                        })()
                                      );
                                    }}
                                  ></InputText>
                                ),
                                action: (
                                  <Select
                                    value={
                                      rechargerActions?.[idx]?.[jj]?.action ??
                                      getDefaultAction(c)
                                    }
                                    onChange={(e) =>
                                      toggleTable(e.target.value, idx, jj, c)
                                    }
                                    style={{
                                      minWidth: 150,
                                      width: 150,
                                      boxSizing: 'border-box',
                                    }}
                                  >
                                    {getAvailableActions(
                                      rechargerActions?.[idx]?.[jj]?.cylinder ??
                                        c
                                    ).map((action) => (
                                      <option key={action} value={action}>
                                        {action === 'recarga'
                                          ? 'Recarga'
                                          : 'Troca'}
                                      </option>
                                    ))}
                                  </Select>
                                ),
                                expandPanel: (
                                  <AvailableExchangeCylinder
                                    patient={patient}
                                    cylinderIdx={jj}
                                    patientIdx={idx}
                                    cylinder={c}
                                  ></AvailableExchangeCylinder>
                                ),
                              }))}
                            ></MuiRechargeTable>
                            <div
                              className="custom-recharge-button-web"
                              style={{ textAlign: 'right', marginTop: '15px' }}
                            >
                              <PrimaryButton onClick={() => moveNext()}>
                                Prosseguir
                              </PrimaryButton>
                            </div>
                          </>
                        ) : (
                          <Spinner></Spinner>
                        )}
                      </PatientInsideBody>
                    </PatientItemBody>
                  </PatientItemHeader>
                </PatientItemList>
              );
            })}
          </PatientList>
        </PatientsListRoot>
      ) : (
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            overflow: 'hidden',
            padding: '20px',
            boxSizing: 'border-box',
          }}
        >
          <Spinner></Spinner>
        </div>
      )}
      <BasicPagination
        style={{ textAlign: 'center' }}
        qtyItems={rows}
        page={Number.isFinite(page) ? page : 0}
        pageSize={Number.isFinite(pageSize) ? pageSize : 10}
        pageSizeOptions={[10, 20, 50]}
        onChange={doPagination}
      ></BasicPagination>
    </div>
  );
};
